'use client'

import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { useIubendaDialogOpen } from './use-iubenda-dialog-open'
import { lazyComponent } from 'utils/lazy-component'
import { GrowthBookProvider, useGrowthBookPlatform } from 'growthbook'
import { Z_INDEX_MODALS } from 'constants/modals'
import type { UseCase, DefaultPlan } from 'cf-types'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { PaymentsAppTypeContext } from './payments-app-type'
import type { PaymentsAppType } from './payments-app-type'
import type { PaymentsData } from '../payments-dialog/context/payments-dialog-types'

// oxlint-disable-next-line cf-web/no-barrel-file
export { type PaymentsAppType, usePaymentsAppType, getModalLocation } from './payments-app-type'

const PaymentsDialog = lazyComponent(() =>
  import('../payments-dialog').then(mod => mod.PaymentsDialog),
)

export interface PaymentsDialogOpenOptions {
  /** Let Escape close the dialog, which otherwise stays open until a plan is chosen or closed. */
  closeOnEscape?: boolean
}

export const PaymentsDialogContext = React.createContext<{
  open: (plan?: DefaultPlan, useCase?: UseCase, options?: PaymentsDialogOpenOptions) => void
}>({
  open: () => {
    throw new Error('PaymentsDialogContext not initialized')
  },
})

const IubendaCoordinationContext = React.createContext(false)

const zIndex = Z_INDEX_MODALS.payment.react

function usePaymentDialogOpen(iubendaOpen: boolean) {
  const pendingOpen = React.useRef(false)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    if (iubendaOpen) {
      setOpen(false)
    } else if (pendingOpen.current) {
      pendingOpen.current = false
      setOpen(true)
    }
  }, [iubendaOpen])

  const requestOpen = () => {
    if (iubendaOpen) {
      pendingOpen.current = true
    } else {
      setOpen(true)
    }
  }

  return { open, setOpen, requestOpen }
}

function PaymentsDialogContent({
  currency,
  defaultPlan,
  useCase,
  openerRef,
  closeOnEscape,
}: {
  currency?: PaymentsData['currency']
  defaultPlan: DefaultPlan
  useCase: UseCase
  openerRef: React.RefObject<HTMLElement | null>
  closeOnEscape: boolean
}) {
  return (
    <Dialog.Portal>
      <Dialog.Body
        dismissable={false}
        overlayZIndex={zIndex - 1}
        zIndex={zIndex}
        data-payments-dialog
        data-lumo-interactive
        // The primitive blocks Escape for non-dismissable dialogs; only an opt-in replaces that.
        {...(closeOnEscape ? { onEscapeKeyDown: () => undefined } : {})}
        onCloseAutoFocus={event => {
          event.preventDefault()
          document.body.style.pointerEvents = ''
          openerRef.current?.focus()
        }}
      >
        <React.Suspense fallback={<AnimatedSpinnerIcon />}>
          <PaymentsDialog.component
            currency={currency}
            defaultPlan={defaultPlan}
            useCase={useCase}
          />
        </React.Suspense>
      </Dialog.Body>
    </Dialog.Portal>
  )
}

export const PaymentsDialogProvider = ({
  children,
  useCase = 'purchase',
  appType = 'marketplace',
  currency,
  coordinateIubenda,
}: {
  children: React.ReactNode
  useCase?: UseCase
  appType?: PaymentsAppType
  currency?: PaymentsData['currency']
  coordinateIubenda?: boolean
}) => {
  const platform = useGrowthBookPlatform(appType === 'marketplace' ? 'marketplace' : 'idea.ai')
  const inheritedCoordination = React.useContext(IubendaCoordinationContext)
  const coordinateConsent = coordinateIubenda ?? inheritedCoordination
  const iubendaOpen = useIubendaDialogOpen(coordinateConsent)
  const { open, setOpen, requestOpen } = usePaymentDialogOpen(iubendaOpen)
  const [defaultPlan, setDefaultPlan] = React.useState<DefaultPlan>('freeTrial')
  const [internalUseCase, setInternalUseCase] = React.useState<UseCase>(useCase)
  const [closeOnEscape, setCloseOnEscape] = React.useState(false)
  const openerRef = React.useRef<HTMLElement | null>(null)
  const wasOpenRef = React.useRef(false)
  if (open && !wasOpenRef.current) {
    const active = document.activeElement
    openerRef.current = active instanceof HTMLElement ? active : null
  }
  wasOpenRef.current = open

  return (
    <IubendaCoordinationContext.Provider value={coordinateConsent}>
      <PaymentsAppTypeContext.Provider value={appType}>
        <GrowthBookProvider platform={platform}>
          <PaymentsDialogContext.Provider
            value={{
              open: (
                plan: DefaultPlan = 'freeTrial',
                useCase: UseCase = 'purchase',
                options?: PaymentsDialogOpenOptions,
              ) => {
                if (document.querySelector('[data-payments-dialog]')) {
                  return
                }
                setDefaultPlan(plan)
                setInternalUseCase(useCase)
                setCloseOnEscape(options?.closeOnEscape === true)
                requestOpen()
              },
            }}
          >
            <Dialog.Root open={open && !iubendaOpen} onOpenChange={setOpen} defaultOpen={false}>
              <PaymentsDialogContent
                currency={currency}
                defaultPlan={defaultPlan}
                useCase={internalUseCase}
                openerRef={openerRef}
                closeOnEscape={closeOnEscape}
              />
            </Dialog.Root>
            {children}
          </PaymentsDialogContext.Provider>
        </GrowthBookProvider>
      </PaymentsAppTypeContext.Provider>
    </IubendaCoordinationContext.Provider>
  )
}

export const usePaymentsDialog = () => {
  const context = React.useContext(PaymentsDialogContext)

  if (context === undefined) {
    throw new Error('usePaymentsDialog must be used within a PaymentsDialogContext')
  }

  return context
}
